/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { en } from './en';

export const pt: typeof en = {
  translation: {
    global: {
      hello: 'Olá',
      ops: 'Ops, algo deu errado',
      dashboard: 'Painel',
      signIn: 'Entrar',
      signOut: 'Sair',
      copyright: 'Direitos Autorais',
      next: 'Próximo',
      back: 'Voltar',
      finish: 'Finalizar',
      copy: 'Copiar',
      copied: 'Copiado',
      failedCopy: 'Falha ao copiar para a área de transferência',
      example: 'Exemplo',
      loading: 'Carregando',
      regenerate: 'Regenerar',
      notAvailable: 'Não disponível',
      apiRequestFailedTitle: 'Falha na solicitação da API',
      apiRequestFailedDescription: '{{method}} request to {{url}} failed: {{error}}',
      forms: {
        required: 'Isso é necessário',
        invalid: 'Inválido',
      },
    },
    assignConfigOverlay: {
      title: 'Selecione uma configuração para atribuir ao nó',
      cancel: 'Cancelar',
      assign: 'Atribuir',
    },
    apiTokens: {
      apiTokenCreated: 'Token de API criado',
      confirmDelete: {
        title: 'Tem certeza de que deseja revogar o token de API “{{name}}”?',
        body1:
          'Revogar este token de API irá desativá-lo imediatamente e ele não poderá mais ser usado para autenticar com a API da Mimic.',
        body2: 'Esta ação não pode ser revertida.',
        cancel: 'Não, cancelar',
        ok: 'Sim, revogar',
      },
      creationError: 'Erro ao criar token de API',
      createTokenModal: {
        nameRequired: 'O nome do token é obrigatório',
        nameMaxLength: 'O nome do token não pode ter mais de {{maxLength}} caracteres',
        namePlaceholder: 'Nome do token',
        permissions: 'Permissões',
        permissionsRequired: 'Pelo menos uma permissão é obrigatória',
        selectAtLeastOnePermission: 'Por favor, selecione pelo menos uma permissão',
        cancel: 'Cancelar',
        create: 'Criar',
        ok: 'Ok',
        title: 'Criar token de API',
      },
      listTitle: 'Tokens de API',
      table: {
        apiTokenName: 'Nome do Token',
        permissions: 'Permissões',
        createdBy: 'Criado por',
        dateCreated: 'Data de Criação (UTC)',
        revoke: 'Revocar',
      },
      revocationError: 'Erro ao revogar o token de API',
      tokenRevoked: 'Token de API revogado',
      copyTokenWarning: 'Esta é a única vez que você pode copiar este token.',
      copyTokenWarningNote: 'Aviso!',
      failedCopy: 'Falha ao copiar o token',
      tokenCopied: 'Token copiado',
      newAPIToken: 'Novo token de API',
      description:
        'Os tokens de API permitem executar ações na API da Mimic. Eles são úteis para automação, scripts ou integrações de terceiros.',
      permissionsSection: {
        title: 'Permissões',
        subtitle: 'Escolha quais permissões este token deve ter',
        loading: 'Carregando permissões...',
        resourceColumn: 'Recurso',
      },
      tokenForm: {
        cancel: 'Cancelar',
        generalInfo: 'Informações Gerais',
        nameLabel: 'Nome',
        nameHelp: 'Um nome para ajudar você a identificar este token',
        namePlaceholder: 'Digite o nome do token',
      },
    },
    installerTokens: {
      listTitle: 'Tokens de Instalação',
      description: 'Gerencie tokens para implantação segura do Mimic Node',
      generateToken: 'Gerar token',
      viewRevokedTokens: 'Ver tokens revogados',
      revokedTokensTitle: 'Tokens revogados',
      revokedTokensDescription: 'Visualize tokens de instalação revogados',
      backToInstallerTokens: 'Voltar para Tokens de Instalação',
      createModalTitle: 'Gerar Token de Instalação',
      createModalDescription:
        'Este token será usado para instalar com segurança o Mimic Node e provisioná-lo com a configuração correta.',
      tokenNameLabel: 'Nome do token',
      tokenNameRequired: 'Nome do token é obrigatório',
      tokenNamePlaceholder: 'Digite o nome',
      tokenNameTooltip: 'Digite um nome único para acompanhar seu token de instalação',
      tokenNameMaxLength: 'O nome do token deve ter no máximo {{maxLength}} caracteres',
      duplicateTokenName: 'Este nome de token já está sendo usado',
      fleetLabel: 'Frota',
      fleetTooltip: 'Nós instalados com este token serão automaticamente atribuídos a esta frota quando elegíveis',
      fleetPlaceholder: 'Selecione uma frota (opcional)',
      fleetLoading: 'Carregando frotas...',
      fleetNotFound: 'Nenhuma frota encontrada',
      generate: 'Gerar',
      successModalTitle: 'Novo Token de Instalação Criado',
      successModalDescription:
        'Copie e salve este ID de token em um local seguro, pois ele só estará disponível neste momento.',
      tokenIdLabel: 'ID do Token de Instalação',
      tokenNotVisibleWarning: 'Este ID de token de instalação não estará visível após fechar este modal',
      copyAndClose: 'Copiar e fechar',
      failedCopy: 'Falha ao copiar o token de instalação',
      creationError: 'Erro ao criar token de instalação',
      tokenGeneratedAndCopied: 'Novo token gerado e copiado para a área de transferência',
      table: {
        jti: 'JTI',
        tokenName: 'Nome do Token',
        createdBy: 'Criado Por',
        dateCreated: 'Data de Criação (UTC)',
        timesUsed: 'Vezes Usado',
        fleet: 'Frota',
        revoke: 'Revogar',
        revokedBy: 'Revogado Por',
        dateRevoked: 'Data de Revogação (UTC)',
      },
      confirmDelete: {
        title: 'Tem certeza de que deseja revogar?',
        body1:
          'Revogar este token de instalação irá desativá-lo imediatamente e ele não poderá mais ser usado para instalar novos nós.',
        cancel: 'Cancelar',
        ok: 'Revogar',
      },
      revocationError: 'Erro ao revogar token de instalação',
      tokenRevoked: 'Token de instalação revogado',
    },
    oauthClients: {
      clientCreated: 'Cliente OAuth criado',
      clientUpdated: 'Cliente OAuth atualizado',
      clientDeleted: 'Cliente OAuth excluído',
      clientNotFound: 'Cliente OAuth não encontrado',
      confirmDelete: {
        title: 'Tem certeza de que deseja excluir "{{name}}"?',
        body1:
          'Excluir este cliente OAuth irá desativá-lo imediatamente e ele não poderá mais ser usado para autenticação.',
        body2: 'Esta ação não pode ser revertida.',
        cancel: 'Não, cancelar',
        ok: 'Sim, excluir',
      },
      creationError: 'Erro ao criar cliente OAuth',
      updateError: 'Erro ao atualizar cliente OAuth',
      deleteError: 'Erro ao excluir cliente OAuth',
      createClientModal: {
        title: 'Criar novo cliente OAuth',
      },
      createdModal: {
        clientIdLabel: 'ID do Cliente',
        clientSecretLabel: 'Segredo do Cliente',
        warning: 'Esta é a única vez que você pode visualizar o segredo do cliente. Salve-o em um local seguro.',
        warningNote: 'Importante!',
        ok: 'Ok',
      },
      listTitle: 'Clientes OAuth',
      newOAuthClient: 'Novo cliente OAuth',
      table: {
        name: 'Nome',
        clientId: 'ID do Cliente',
        redirectUris: 'URIs de Redirecionamento',
        dateCreated: 'Data de Criação (UTC)',
        actions: 'Ações',
        view: 'Visualizar',
        delete: 'Excluir',
        more: 'mais',
      },
      failedCopy: 'Falha ao copiar',
      description:
        'Clientes OAuth permitem que aplicativos de terceiros autentiquem usuários e acessem recursos em seu nome. Configure URIs de redirecionamento e outras configurações para habilitar fluxos seguros OAuth 2.0.',
      clientForm: {
        cancel: 'Cancelar',
        create: 'Criar',
        generalInfo: 'Informações Gerais',
        nameLabel: 'Nome',
        nameRequired: 'O nome do cliente OAuth é obrigatório',
        nameMaxLength: 'O nome do cliente OAuth não pode ter mais de {{maxLength}} caracteres',
        nameHelp: 'Um nome descritivo para identificar este cliente OAuth',
        namePlaceholder: 'Digite o nome do cliente',
        redirectUrisTitle: 'URIs de Redirecionamento',
        redirectUrisLabel: 'URI de Redirecionamento',
        redirectUrisRequired: 'Pelo menos um URI de redirecionamento é obrigatório',
        redirectUriRequired: 'URI de redirecionamento é obrigatório',
        redirectUriInvalid: 'Por favor, insira uma URL válida (http:// ou https://)',
        redirectUrisHelp:
          'URLs para onde os usuários serão redirecionados após autenticação. Adicione vários URIs se necessário.',
        redirectUriPlaceholder: 'https://exemplo.com/callback',
        addRedirectUri: 'Adicionar URI de redirecionamento',
        advancedSettings: 'Configurações Avançadas',
        signatureAlgLabel: 'Algoritmo de Assinatura do Token de ID',
        signatureAlgHelp: 'Algoritmo usado para assinar tokens de ID. Deixe vazio para usar o padrão (RS256).',
        signatureAlgOnlyRS256: 'RS256 é o único algoritmo suportado atualmente.',
        signatureAlgPlaceholder: 'Selecione o algoritmo',
      },
      detailsPage: {
        basicInfo: 'Informações Básicas',
        configuration: 'Configuração',
        clientId: 'ID do Cliente',
        name: 'Nome',
        redirectUris: 'URIs de Redirecionamento',
        confidential: 'Confidencial',
        pkce: 'PKCE',
        accessTokenTtl: 'TTL do Token de Acesso',
        authorizationCodeTtl: 'TTL do Código de Autorização',
        refreshTokenTtl: 'TTL do Token de Atualização',
        idTokenSignatureAlg: 'Algoritmo de Assinatura do Token de ID',
        supportedGrantTypes: 'Tipos de Concessão Suportados',
        yes: 'Sim',
        no: 'Não',
        enabled: 'Habilitado',
        disabled: 'Desabilitado',
      },
      back: 'Voltar',
      backToList: 'Voltar para Clientes OAuth',
      edit: 'Editar',
      delete: 'Excluir',
      saveChanges: 'Salvar alterações',
    },
    m2mClients: {
      listTitle: 'Clientes M2M',
      newM2MClient: 'Novo cliente M2M',
      description:
        'Clientes M2M permitem que aplicativos de servidor se autentiquem diretamente com a API Mimic usando credenciais de cliente.',
      clientCreated: 'Cliente M2M criado',
      clientDeleted: 'Cliente M2M excluído',
      creationError: 'Erro ao criar cliente M2M',
      deleteError: 'Erro ao excluir cliente M2M',
      failedCopy: 'Falha ao copiar',
      confirmDelete: {
        title: 'Tem certeza de que deseja excluir "{{name}}"?',
        body1:
          'Excluir este cliente M2M irá desativá-lo imediatamente e ele não poderá mais ser usado para autenticação da API.',
        body2: 'Esta ação não pode ser revertida.',
        cancel: 'Não, cancelar',
        ok: 'Sim, excluir',
      },
      table: {
        clientId: 'ID do Cliente',
        name: 'Nome',
        allowedScopes: 'Escopos Permitidos',
        actions: 'Ações',
        view: 'Visualizar',
        delete: 'Excluir',
        more: 'mais',
      },
      backToList: 'Voltar para Clientes M2M',
      delete: 'Excluir',
      clientNotFound: 'Cliente M2M não encontrado',
      detailsPage: {
        basicInfo: 'Informações Básicas',
        clientId: 'ID do Cliente',
        name: 'Nome',
        allowedScopes: 'Escopos Permitidos',
        configuration: 'Configuração',
        accessTokenTtl: 'TTL do Token de Acesso',
        supportedGrantTypes: 'Tipos de Concessão Suportados',
      },
      createClient: {
        title: 'Criar novo cliente M2M',
      },
      form: {
        generalInfo: 'Informações Gerais',
        nameLabel: 'Nome',
        nameRequired: 'O nome do cliente M2M é obrigatório',
        nameMaxLength: 'O nome não pode exceder 70 caracteres',
        nameHelp: 'Um nome descritivo para identificar este cliente M2M',
        namePlaceholder: 'Digite o nome do cliente',
        scopesTitle: 'Escopos Permitidos',
        scopesHelp: 'Escopos definem quais recursos este cliente pode acessar',
        scopesRequired: 'Pelo menos um escopo é obrigatório',
        scopesPlaceholder: 'Selecionar escopos...',
        cancel: 'Cancelar',
        create: 'Criar',
      },
      createdModal: {
        clientIdLabel: 'ID do Cliente',
        clientSecretLabel: 'Segredo do Cliente',
        warning: 'Esta é a única vez que você pode visualizar o segredo do cliente. Salve-o em um local seguro.',
        warningNote: 'Importante!',
        ok: 'Ok',
      },
    },
    configWizardOverlay: {
      selectConfiguration: 'Selecione a configuração',
      selectRevision: 'Selecione a revisão',
      node: 'Nó',
      configuration: 'Configuração',
      configurations: 'Configurações',
      revision: 'Revisão',
      revisions: 'Revisões',
      assign: 'Atribuir',
      cancel: 'Cancelar',
      change: 'Alterar',
      next: 'Próximo',
      previous: 'Anterior',
      reviewConfigurations: 'Revise as configurações',
      reviewConfigurationJson: 'Revisar JSON de configuração',
    },
    footer: {
      copyright: '© 2026 Mimic Networks, Inc.',
      contactUs: 'Preciso de ajuda? Contate-nos',
    },
    jobs: {
      listTitle: 'Tarefas',
      end: 'Fim',
      jobDetailsTitle: 'Tarefa',
      revision: 'Revisão',
      start: 'Início',
      targetConfig: 'Configuração almejada',
      viewJob: 'Ver tarefa',
      pauseBanners: 'Pausar alertas de banner',
      active: 'Ativo',
      inactive: 'Inativo',
      jobDetailsTableTitle: 'Nós',
      jobNodeIssuesModal: {
        noContentFound: 'Nenhum conteúdo encontrado',
        warnings: 'Avisos',
        errors: 'Erros',
      },
      jobNodes: {
        hostName: 'Nome do anfitrião',
        nodeVersion: 'Versão do nó ({{startDate}})',
        os: 'OS',
        status: 'Status',
        processing: 'Processamento',
        configured: 'Configurado',
        rejected: 'O nó rejeitou a configuração',
        warning: 'Configurado com aviso',
      },
      table: {
        jobName: 'Nome',
        subtenant: 'Área',
        targetConfig: 'Configuração',
        os: 'SO',
        timedout: 'Tempo esgotado',
        failed: 'Falhas',
        warning: 'Avisos',
        success: 'Sucesso',
        started: 'Iniciado',
        startedBy: 'Por',
      },
      banner: {
        dismissJobBannerError: 'Erro ao dispensar o banner de tarefa',
        jobStatusError: 'Erro ao atualizar o status da tarefa',
        error: 'erro',
        warning: 'aviso',
        running: 'em execução',
        runningJob: 'tarefa em execução',
        timeout: 'tempo esgotado',
        timedOutJob: 'tarefa com tempo esgotado',
        aggregated:
          '{{notificationType}}s foram agregados e podem ser visualizados individualmente na página de Tarefas',
        viewJobs: 'Ver Tarefas',
        pauseBannerAlerts: 'Pausar todos os alertas de banner',
        bannersPaused: 'Alertas de banner pausados',
      },
      pauseBannersModal: {
        title: 'Pausar alertas de banner',
        description:
          'Os alertas de banner serão pausados por 24 horas. Você pode reativá-los desligando o interruptor na página de Tarefas.',
        confirm: 'Pausar',
        cancel: 'Cancelar',
      },
    },
    configureNodesJobWizard: {
      configureNodes: 'Configurar nós',
      selectNodes: 'Selecione nós',
      selectConfig: 'Selecione a configuração',
      reviewJob: 'Revisar tarefa',
      startJob: 'Iniciar tarefa',
      previous: 'Anterior',
      nodes: 'Nós',
      selectANode: 'Selecione pelo menos um nó',
      selectARevision: 'Selecione uma revisão de uma configuração',
      nodesSelected: 'nós selecionados',
      configName: 'Nome da configuração',
      assignedTo: 'Atribuído a',
      lastRevised: 'Última revisão',
      nodesLower: 'nós',
      rev: 'Revisão',
      configuringNodesCreates: 'Configurar nós cria um tarefa',
      jobName: 'Nome do tarefa',
      targetConfig: 'Configuração de destino',
      os: 'OS',
      tags: 'Tags',
      nodeVersion: 'Versão do nó',
      operationalState: 'Estado operacional',
      hostName: 'Nome do host',
      unableToStartJob: 'Erro inesperado ao tentar iniciar a tarefa',
      jobNameRequired: 'Por favor, informe um nome para a tarefa',
    },
    pagination: {
      summary: 'Mostrando {{start}}-{{end}} de {{total}} resultados',
      selectedItems: '{{count}} itens selecionados',
    },
    menu: {
      activity: 'Atividade',
      dashcam: 'Dashcam',
      subtenants: 'Áreas',
      apiTokens: 'Tokens de API',
      installerTokens: 'Tokens de Instalação',
      oauthClients: 'Clientes OAuth',
      m2mClients: 'Clientes M2M',
      nodes: 'Nós',
      telemetry: 'Telemetria',
      reports: 'Relatórios',
      driftReports: 'Relatórios de Desvio',
      changeAudit: 'Auditoria de Mudanças',
      biDashboards: 'Painéis de BI',
      configurations: 'Configurações',
      notifications: 'Notificações',
      feed: 'Feed',
      management: 'Gerenciamento',
      releaseManagement: 'Gerenciamento de Releases',
      mimicCli: 'Mimic CLI',
      download: 'Download',
      downloadNode: 'Baixar Node',
      extensibility: 'Extensibilidade',
      nodePlugins: 'Node',
      authenticate: 'Autenticar',
      jobs: 'Tarefas',
      users: 'Usuários',
      userActivity: 'Atividade do Usuário',
      tags: 'Tag',
      logout: 'Sair',
      docs: 'Docs',
      apiReference: 'Referência da API',
      systemInfo: 'Sobre essa plataforma',
      windows: 'Windows',
      linux: 'Linux',
      contactSupport: 'Contatar Suporte',
    },
    notifications: {
      title: 'Notificações',
      columns: {
        notification: 'Notificação',
        urgency: 'Urgência',
        timestamp: 'Tempo',
        channels: 'Canais',
      },
      clear_node_telemetry: {
        hallmarks: {
          subtitle: '{{user}} limpou as marcas do nó',
        },
        deflections: {
          subtitle: '{{user}} limpou as deflexões do nó',
        },
        signals: {
          subtitle: '{{user}} limpou os sinais do nó',
        },
        reason: 'Razão: {{reason}}',
      },
    },
    notificationManagement: {
      title: 'Gerenciamento de Notificações',
      subtitle: 'Envie automaticamente alertas de segurança para as ferramentas preferidas da sua equipe',
      setupTitle: 'Configurar uma nova conexão',
      setupSubtitle: 'Selecione para onde deseja enviar notificações',
      selectDestination: 'Selecione um destino',
      addConnection: 'Adicionar conexão',
      cancel: 'Cancelar',
      createSuccess: 'Configuração de alerta criada com sucesso',
      createError: 'Falha ao criar configuração de alerta',
      unsupportedType: 'Este tipo de destino ainda não é suportado',
      newConnection: 'Nova Conexão',
      table: {
        connectionName: 'Nome da Conexão',
        destination: 'Destino',
        status: 'Status',
        created: 'Criado',
      },
      steps: {
        setupConnection: 'Configurar Conexão',
        setParameters: 'Definir Parâmetros',
        review: 'Revisar',
      },
      setupConnection: {
        title: 'Configurar Conexão {{destination}}',
        description: 'Você precisará configurar o Mimic como um aplicativo no {{destination}}.',
        connectionName: 'Nome da Conexão',
        descriptionLabel: 'Descrição',
        descriptionPlaceholder: 'Opcional',
        webhookUrl: 'URL do Webhook',
        test: 'Testar',
        token: 'Token',
        disableTls: 'Desativar verificação TLS',
        nextLabel: 'Selecionar Notificações',
      },
      parameters: {
        title: 'Selecionar Parâmetros de Notificação',
        subtitle:
          'Notificações ao vivo do seu tenant serão enviadas para sua instância {{destination}}. Defina seus parâmetros abaixo.',
        eventTypesTitle: 'Tipos de evento',
        eventTypesDescription:
          'Ocorrências ou atividades brutas detectadas pelo sistema que representam algo acontecendo em um endpoint ou rede',
        events: {
          signal: 'Sinais',
          signalDescription:
            'Pontos de dados individuais coletados de sensores de endpoint indicando atividade potencialmente relevante para segurança.',
          signalWarning: 'Sinais podem ser muito ruidosos',
          deflection: 'Deflexões',
          deflectionDescription:
            'Ameaças bloqueadas ou mitigadas onde o sistema impediu com sucesso a atividade maliciosa.',
          hallmark: 'Hallmarks',
          hallmarkDescription:
            'Indicadores de comprometimento de alta confiança que correspondem a padrões ou assinaturas de ataque conhecidos.',
          lifecycle: 'Ciclo de Vida',
          lifecycleDescription:
            'Alterações no status do nó, como conexões, desconexões e atualizações de configuração.',
        },
        nodesTitle: 'Nós',
        nodesDescription:
          'Selecione nós específicos para receber notificações, ou deixe vazio para incluir todos os nós.',
        addNodes: 'Adicionar Nós',
        tagsTitle: 'Tags',
        tagsDescription: 'Filtre alertas por tags de nó.',
        includeTitle: 'Pares Chave : Valor Incluídos',
        includeDescription: 'Enviar notificações apenas para eventos que correspondam a todos esses pares chave-valor.',
        excludeTitle: 'Pares Chave : Valor Excluídos',
        excludeDescription:
          'Suprimir notificações para eventos que correspondam a qualquer um desses pares chave-valor.',
        keyPlaceholder: 'Chave...',
        valuePlaceholder: 'Valor...',
        add: 'Adicionar',
        nextLabel: 'Revisar',
        previousLabel: 'Conectar ao {{destination}}',
      },
      selectNodes: {
        title: 'Selecionar Nós',
        description: 'Escolha quais nós devem disparar notificações para esta conexão.',
        hostName: 'Nome do Host',
        nodeId: 'ID do Nó',
        nodeTags: 'Tags do Nó',
        searchPlaceholder: 'Buscar nós',
        addNodes: 'Adicionar {{count}} nó',
        addNodes_other: 'Adicionar {{count}} nós',
      },
      review: {
        title: 'Revisar Configuração',
        subtitle: 'Revise sua configuração de notificação antes de criá-la.',
        destination: 'Destino',
        connectionName: 'Nome da Conexão',
        description: 'Descrição',
        webhookUrl: 'URL do Webhook',
        eventTypes: 'Tipos de Evento',
        nodes: 'Nós',
        allNodes: 'Todos os nós (sem filtro)',
        tags: 'Tags',
        includeFilters: 'Pares Chave:Valor Incluídos',
        excludeFilters: 'Pares Chave:Valor Excluídos',
        none: 'Nenhum selecionado',
        submit: 'Criar Alerta',
        update: 'Salvar Alterações',
        previousLabel: 'Definir Parâmetros',
      },
      details: {
        back: 'Voltar',
        notFound: 'Configuração de alerta não encontrada.',
        active: 'Ativo',
        suspended: 'Suspenso',
        created: 'Criado',
        edit: 'Editar',
        suspendConnection: 'Suspender Conexão',
        activateConnection: 'Ativar Conexão',
        connection: 'Conexão',
        eventTypes: 'Tipos de Evento',
        nodes: 'Nós',
        allNodes: 'Todos os nós (sem filtro aplicado)',
        tags: 'Tags',
        includedKeyValuePairs: 'Pares Chave : Valor Incluídos',
        excludedKeyValuePairs: 'Pares Chave : Valor Excluídos',
        signalWarning: 'Sinais podem ser muito ruidosos',
        statusUpdated: 'Status da conexão atualizado',
        statusUpdateError: 'Falha ao atualizar status da conexão',
        configUpdated: 'Configuração atualizada',
        configUpdateError: 'Falha ao atualizar configuração',
        deleteConnection: 'Excluir Conexão',
        deleteSuccess: 'Conexão excluída com sucesso',
        deleteError: 'Falha ao excluir conexão',
        viewResponses: 'Ver Respostas',
        overview: 'Visão Geral',
        responses: 'Respostas',
      },
      responses: {
        title: 'Respostas de Alerta',
        subtitle: 'Visualize o histórico de entrega de alertas e teste sua conexão',
        backToDetails: 'Voltar para Detalhes do Alerta',
        eventType: 'Tipo de Evento',
        status: 'Status',
        timestamp: 'Timestamp',
        testBadge: 'TESTE',
        generateTest: 'Gerar Evento de Teste',
        waitingForResponse: 'Aguardando resposta...',
        testAlertQueued: 'Alerta de teste enfileirado com sucesso',
        testAlertReceived: 'Resposta do alerta de teste recebida',
        testAlertTimeout:
          'Resposta do alerta de teste não disponível após 1 minuto. Por favor, verifique novamente mais tarde.',
        testAlertError: 'Falha ao enviar alerta de teste',
        statusCode: 'Código de Status',
        error: 'Erro',
        yes: 'Sim',
        no: 'Não',
        errorMessage: 'Mensagem de Erro',
        responseBody: 'Corpo da Resposta',
        telemetryId: 'ID de Telemetria',
      },
      confirmDelete: {
        title: 'Excluir "{{name}}"?',
        body1: 'Isso excluirá permanentemente esta conexão de notificação. Toda a configuração será perdida.',
        body2: 'Esta ação não pode ser desfeita.',
        cancel: 'Cancelar',
        ok: 'Excluir',
      },
    },
    systemInfo: {
      title: 'Sobre essa plataforma',
      mgmtPlaneVersion: 'Versão do Management Plane',
      dashboardVersion: 'Versão do Dashboard',
    },
    tags: {
      title: 'Tag',
      subtitle: 'Gerencie suas tags.',
      newTag: 'Nova tag',
      tags: 'Tag',
      name: 'Nome',
      useCount: 'Usar contagem',
      confirmDelete: {
        title: 'Tem certeza de que deseja arquivar a tag “{{name}}”?',
        body1:
          'Esta tag está atualmente associada a nós {{useCount}}. Excluí-lo removerá a tag dos nós e a tag não estará mais disponível.',
        body2: 'Esta ação não pode ser revertida.',
        cancel: 'Não, cancelar',
        ok: 'Sim, excluir',
      },
      actions: {
        deleteTag: 'Excluir tag',
        editTagButton: 'Editar',
      },
      tagForm: {
        nameRequired: 'O nome é obrigatório.',
        nameMaxLength: 'O nome da tag não pode ter mais de {{maxLength}} caracteres.',
      },
      addTag: {
        namePlaceholder: 'Nomeie a nova tag...',
        submitButton: 'Adicionar',
      },
      editTag: {
        title: 'Editar nome da tag',
        namePlaceholder: 'Nomeie tag...',
        cancelButton: 'Cancelar',
        submitButton: 'Salvar',
      },
      newTagCreated: 'A nova tag "{{name}}" foi criada',
      newTagCreatedError: 'Erro ao criar tag: {{name}}',
      tagUpdated: 'O nome da tag foi atualizado de "{{oldName}}" para "{{newName}}"',
      tagUpdateError: 'Erro ao atualizar o nome da tag com: {{name}}',
      tagDeleted: 'A tag "{{name}}" foi excluída e removida de {{useCount}} nós associados.',
      tagDeleteError: 'Erro ao excluir a tag: {{name}}',
      addTagError: 'Erro ao adicionar tag: {{name}}',
      removeTagError: 'Erro ao remover a tag: {{name}}',
      updateBaselineError: 'Erro ao atualizar a linha de base',
      updateBaselineSuccess: 'Linha de base atualizada',
    },
    subtenants: {
      title: 'Áreas',
      subtitle: 'Gerencie suas áreas.',
      newSubtenant: 'Nova área',
      subtenant: 'Área',
      subtenants: 'Áreas',
      parent: 'Pai',
      name: 'Nome',
      confirmDelete: {
        title: 'Excluir área',
        body1: 'Você tem certeza de que deseja excluir a área “{{name}}”?',
        cancel: 'Cancelar',
        ok: 'Sim, excluir área',
        subtenantNotFound: '{{name}} não encontrada',
        subtenantDeleteConflict: '{{name}} contém recursos ou nós associados',
        subtenantDeleteError: 'Erro ao excluir {{name}}',
      },
      actions: {
        deleteSubtenant: 'Excluir área',
        editSubtenantButton: 'Editar área',
        users: 'Usuários',
        backToSubtenants: 'Voltar para Áreas',
      },
      subtenantForm: {
        nameRequired: 'O nome é obrigatório.',
        nameMaxLength: 'O nome da área não pode ter mais de {{maxLength}} caracteres.',
        descriptionMaxLength: 'Descrição não pode ter mais de {{maxLength}} caracteres.',
        ok: 'Ok',
        cancel: 'Cancelar',
      },
      addSubtenant: {
        namePlaceholder: 'Nomeie a nova área...',
        submitButton: 'Adicionar',
        modal: 'Nova Área',
        subtenantName: 'Nome da Área',
        description: 'Descrição',
        badgePreview: 'Visualização do Distintivo',
        parentSubtenant: 'Área Pai',
        parentSubtenantPlaceholder: 'Selecione uma área pai...',
        parentSubtenantLoading: 'Carregando...',
        parentSubtenantNotFound: 'Nenhuma área encontrada',
      },
      editSubtenant: {
        title: 'Editar nome da área',
        namePlaceholder: 'Nomeie a área...',
        cancelButton: 'Cancelar',
        submitButton: 'Salvar',
      },
      deleteSubtenant: {
        disableDelete: 'Deve estar vazio para excluir',
      },
      newSubtenantCreated: 'A nova área "{{name}}" foi criada',
      newSubtenantCreatedError: 'Erro ao criar área: {{name}}',
      subtenantUpdated: 'O nome da área foi atualizado de "{{oldName}}" para "{{newName}}"',
      subtenantUpdateError: 'Erro ao atualizar o nome da área com: {{name}}',
      subtenantDeleted: 'A área "{{name}}" foi excluída',
      subtenantLoadingError: 'Erro ao carregar a área de trabalho',
      subtenantLoading: 'Carregando áreas de trabalho...',
      noSubtenants: 'Nenhuma área de trabalho encontrada',
      errorFetchingSubtenant: 'Erro ao buscar área de trabalho',
      errorFetchingSubtenants: 'Erro ao buscar áreas de trabalho',
      searchForMore: 'Buscar mais...',
      description: 'Descrição',
      noDescriptionProvided: 'Nenhuma descrição fornecida',
      parentSubtenant: 'Área Pai',
      members: 'Membros',
      user: 'Usuário',
      email: 'Email',
      role: 'Função',
      roles: {
        admin: 'Administrador',
        editor: 'Editor',
        viewer: 'Visualizador',
        noAccess: 'Sem acesso',
        inherit: 'Herdar',
      },
      errors: {
        unableToUpdateMembership: 'Não foi possível atualizar a participação na área',
      },
    },
    users: {
      title: 'Usuários',
      subtitle: 'Gerenciar o acesso ao área',
      newUser: 'Novo usuário',
      active: 'Ativo',
      removed: 'Removido',
      back: 'Voltar',
      name: 'Nome',
      role: 'Função',
      dateAdded: 'Data Adicionada',
      firstName: 'Primeiro Nome',
      lastName: 'Sobrenome',
      email: 'Endereço de e-mail da empresa',
      invite: {
        title: 'Adicionar usuário ao área',
        firstNamePlaceholder: 'Introduza o primeiro nome',
        lastNamePlaceholder: 'Insira o último nome',
        emailPlaceholder: 'Insira o e-mail',
        subtenantRole: 'Função do área',
        permissions: 'Convidar como',
        permissionsPlaceholder: 'Selecione permissões',
        roleMissingError: 'Selecione as permissões',
        enterValidEmail: 'Endereço de e-mail da empresa',
        required: 'Insira o seu sobrenome',
        buttonTextUnconfirmed: 'Adicionar membro',
        buttonTextPending: 'Adicionando...',
        buttonTextConfirmed: 'Adicionado!',
      },
      roles: {
        superadmin: 'Super Admin',
        admin: 'Administrador',
        editor: 'Editor',
        viewer: 'Somente visualizar',
        adminMessage: 'Acesso total com todas as permissões',
        editorMessage: 'Acesso com permissões limitadas',
        viewerMessage: 'Acesso com permissões somente leitura',
      },
      updateSubtenants: {
        assignSubtenantsAndRoles: 'Atribuir áreas e funções',
        assignRolesDescription: 'Atualizar acesso do usuário às áreas',
        assign: 'Atribuir',
        name: 'Nome',
        role: 'Função',
        noAccess: 'Sem acesso',
        inherit: 'Herdar',
        admin: 'Administrador',
        editor: 'Editor',
        viewer: 'Visualizador',
      },
      actions: {
        makeSuperadmin: 'Faça um super administrador',
        makeAdmin: 'Faça um administrador',
        makeEditor: 'Faça um editor',
        makeViewer: 'Faça um visualizador',
        makeSubtenantOnly: 'Limitar acesso a subtenants',
        makeTenantUser: 'Habilitar acesso a tenant',
        remove: 'Remove esta conta',
        restore: 'Restaurar',
        update: 'Atualizar informações do membro',
        view: 'Visualizar',
        delete: 'Excluir',
        assignToSubtenant: 'Atribuir ao área',
        editUser: 'Editar usuário',
        deleteUser: 'Excluir usuário',
      },
      columns: {
        role: 'Permissões',
        dateCreated: 'Data Adicionado',
        subtenantOnly: 'Somente Subtenant',
        subtenantOnlyTooltip: 'Usuários com acesso somente a subtenants não podem acessar recursos do tenant.',
      },
      feedback: {
        userInvited: '{{user}} foi adicionado como membro em {{email}}',
        userUpdated: '{{user}} atualizado',
        userAdded: 'Funções de subtenant atualizadas para {{user}}',
        errorOnInvite: 'Erro ao convidar {{user}}',
        errorOnUpdate: 'Erro ao atualizar {{user}}',
      },
      update: {
        title: 'Atualizar informações do membro',
        buttonTextUnconfirmed: 'Atualizar',
        buttonTextPending: 'Atualizando...',
        buttonTextConfirmed: 'Atualizado!',
      },
      delete: {
        title: 'Excluir Usuário',
        description: 'Tem certeza de que deseja excluir {{userName}} desta área?',
        cancel: 'Cancelar',
        confirm: 'Sim, excluir usuário',
      },
      superadminConfirm: {
        title: 'Tornar Super Administrador',
        description:
          'Tem certeza de que deseja tornar {{userName}} um super administrador? Isso dará acesso total a todos os tenants e configurações do sistema.',
        cancel: 'Cancelar',
        confirm: 'Sim, Tornar Super Administrador',
      },
    },
    mimicSyntaxHighlighter: {
      copy: 'Copiar',
      copied: 'Copiado',
      failedCopy: 'Falha ao copiar para a área de transferência',
    },
    person: {
      name: 'Nome',
      email: 'Email',
      password: 'Senha',
    },
    logIn: {
      welcome: 'Bem-vindo à Mimic',
      instructions: 'Digite seu endereço de email comercial para entrar.',
      email: 'Digite endereço de email...',
      continue: 'Continuar',
      rememberMe: 'Lembrar meu email',
      emailMismatch: 'Email informado não coincide com o email da conta',
      unexpectedError: 'Erro inesperado',
    },
    nodes: {
      title: 'Nós',
      failedToLoad: 'Falha ao carregar nós',
      configured: 'Configurados',
      pending: 'Pendentes',
      archived: 'Arquivados',
      configureNodes: 'Configurar nós',
      table: {
        header: {
          subtenant: 'Área',
          dateCreated: 'Criado (UTC)',
          host_name: 'Nome do Host',
          lastSeen: 'Visto pela última vez (UTC)',
          nickname: 'Apelido',
          tags: 'Tags',
          status: 'Status',
          vigilance: 'Vigilância',
          os: 'OS',
          customer: 'Cliente',
          connectivity: 'Conectividade (UTC)',
          operationalState: 'Estado Operacional',
          configuration: 'Configuração',
          configurationUpdated: 'Atualizado por {{updatedBy}} em {{updatedAt}} (UTC)',
          nodeVersion: 'Versão do Nó',
          config: 'Configuração',
          mode: 'Modo',
          configurationStatus: 'Status da Configuração',
        },
        maintenance: 'Manutenção',
        standard: 'Padrão',
        audit: 'Auditoria',
        view_button: 'Visualizar',
        notAvailable: 'não disponível',
        assignConfiguration: 'Atribuir configuração',
        configurationInProgress: 'Configuração em andamento',
        build: 'Build {{buildNumber}}',
        synced: 'Sincronizado',
        outOfSync: 'Fora de Sincronia',
        windows: 'Windows',
        linux: 'Linux',
      },
      banner: {
        selected: 'selecionado',
        selectAll: 'Selecione tudo',
        deselectAll: 'Desmarcar tudo',
        differentSubtenantWarning: 'Os nós estão em diferentes áreas de trabalho',
        differentOSWarning: 'Os nós têm sistemas operacionais diferentes',
        noSystemProfileInfo: 'Nenhum sistema operacional detectado',
        differentSubtenantError: 'Os nós devem estar na mesma área de trabalho para serem configurados juntos',
        differentOSError: 'Os nós devem ter o mesmo sistema operacional para serem configurados juntos',
        configureNodes: 'Configurar nós',
        onlyDisconnectedCanBeArchived: 'Apenas nós desconectados podem ser arquivados',
      },
      subtenant: {
        nodeChangedSubtenantFail: 'Falha ao alterar a área do nó de "{{oldSubtenant}}" para "{{newSubtenant}}"',
        nodeChangedSubtenantSuccess: 'Nó movido de área "{{oldSubtenant}}" para "{{newSubtenant}}"',
      },
      actions: 'Ações',
      toggleState: 'Alternar Estado',
      toggleStateModal: {
        title: 'Alterações Pendentes',
        hostname: 'Nome do Host',
        requestedState: 'Estado Solicitado',
      },
      toggleMaintenance: 'Alternar Manutenção',
      toggleMaintenanceModeModal: {
        title: 'Alternar Modo de Manutenção',
        hostname: 'Nome do Host',
        currentMode: 'Modo Atual',
        enterButton: 'Entrar em Manutenção',
        exitButton: 'Sair para Padrão',
        cancel: 'Cancelar',
        enterSuccess: 'Nós estão entrando em modo de manutenção',
        exitSuccess: 'Nós estão saindo do modo de manutenção',
        enterError: 'Falha ao entrar no modo de manutenção',
        exitError: 'Falha ao sair do modo de manutenção',
        mixedSelectionWarning:
          'Nós selecionados têm modos diferentes. Selecione apenas nós padrão ou apenas nós em manutenção para prosseguir.',
      },
      createFleet: 'Criar Frota',
      createFleetWizard: {
        title: 'Criar Frota',
        steps: {
          reviewNodes: 'Revisar Nós Selecionados',
          selectConfig: 'Selecionar Configuração',
          selectRevision: 'Selecionar Revisão',
          details: 'Detalhes da Frota',
          review: 'Revisar e Criar',
        },
        reviewNodes: {
          description: '{{count}} nós selecionados',
          inFleetError:
            '{{count}} nó(s) já estão em uma frota e não podem ser adicionados a uma nova frota. Remova-os para prosseguir.',
          inFleet: 'Em Frota',
          remove: 'Remover Selecionados',
          columns: {
            hostname: 'Nome do Host',
            operatingSystem: 'Sistema Operacional',
            connectivity: 'Conectividade',
            lastSeen: 'Visto por Último',
          },
        },
        details: {
          name: 'Nome da Frota',
          namePlaceholder: 'Digite o nome da frota',
          nameRequired: 'Nome da Frota é obrigatório',
          nameMaxLength: 'O nome deve ter no máximo {{max}} caracteres',
          description: 'Descrição',
          descriptionPlaceholder: 'Digite a descrição (opcional)',
          operatingSystem: 'Sistema Operacional',
          osWindows: 'Windows',
          osLinux: 'Linux',
        },
        previous: 'Anterior',
        next: 'Próximo',
        cancel: 'Cancelar',
        createFleet: 'Criar Frota',
        feedback: {
          success: 'Frota criada com sucesso',
          error: 'Falha ao criar frota',
        },
      },
      archiveSelected: 'Arquivar selecionados',
      archiveSelectedNodesModal: {
        title: 'Arquivar nós selecionados?',
        description:
          '{{count}} nós selecionados serão movidos para a aba Arquivados e não aparecerão mais nas operações de nós configurados.',
        andMore: 'e mais {{count}}',
        cancel: 'Cancelar',
        success: 'Nós arquivados',
        errorFallback: 'Falha ao arquivar nós',
      },
    },
    configRevisions: {
      title: 'Revisões',
      table: {
        revision: 'Revisão',
        author: 'Autor',
        id: 'ID',
        createdAt: 'Criado em (UTC)',
      },
    },
    nodeConfigurations: {
      title: 'Configurações',
      active: 'Ativo',
      archived: 'Arquivado',
      uploadConfig: 'Enviar Configuração',
      table: {
        header: {
          name: 'Nome',
          assigned: 'Atribuído a',
          revision: 'Última revisão',
          tags: 'Tags',
          os: 'OS',
          lastRevised: 'Ultima revisão (UTC)',
          id: 'ID',
        },
        nameNotAvailable: 'não disponível',
        nodeCount: '{{nodeCount}} nós',
        outOfDate: '{{nodeCount}} nós desatualizados',
      },
      upload: {
        modal: {
          title: 'Enviar Configuração',
          titleRevision: 'Enviar Revisão para {{configName}}',
          titleParentSelected: 'Enviar Revisão de Configuração',
          cancel: 'Cancelar',
          uploadConfiguration: 'Enviar Configuração',
          uploadRevision: 'Enviar Revisão',
          configurationFileRequired: 'Arquivo de configuração é obrigatório',
          configurationUploadedSuccessfully: 'Configuração enviada com sucesso',
          configurationRevisionUploadedSuccessfully: 'Revisão de configuração enviada com sucesso',
          failedToUploadConfiguration: 'Falha ao enviar configuração',
          failedToUploadConfigurationRevision: 'Falha ao enviar revisão de configuração',
          errorBannerFailed: 'O arquivo falhou ao enviar devido ao(s) seguinte(s) erro(s):',
          errorBannerCorrection: 'Por favor, corrija o(s) erro(s) e tente novamente.',
        },
        form: {
          parentConfiguration: 'Configuração Pai (Opcional)',
          parentConfigurationHelp: 'Selecione uma configuração existente para herdar configurações',
          parentConfigurationPlaceholder: 'Pesquisar configuração existente...',
          configurationFile: 'Arquivo de Configuração',
          configurationFileUploadText: 'Clique ou arraste arquivo JSON para enviar',
          configurationFileUploadSubtext: 'Tamanho máximo do arquivo: 10MB',
          configurationSchemaVersion: 'Versão do Schema de Configuração',
          configurationSchemaVersionHelp: 'Identificador de versão para esta configuração (ex.: v1.0.1, v1.0.1)',
          configurationSchemaVersionPlaceholder: 'ex.: v1.0.1',
          configurationName: 'Nome da Configuração',
          configurationNamePlaceholder: 'Digite o nome da configuração',
          operatingSystem: 'Sistema Operacional',
          operatingSystemPlaceholder: 'Selecione o sistema operacional',
          windows: 'Windows',
          linux: 'Linux',
          loading: 'Carregando...',
          noConfigurationsFound: 'Nenhuma configuração encontrada',
          errors: {
            fileRequiredJson: 'Arquivo deve ser um arquivo JSON',
            fileTooLarge: 'Arquivo deve ser menor que 10MB',
            invalidJsonFile: 'Arquivo JSON inválido',
            configurationFileRequired: 'Arquivo de configuração é obrigatório',
            configurationSchemaVersionRequired: 'Versão do schema de configuração é obrigatória',
            configurationSchemaVersionTooLong: 'Versão do schema não pode exceder {{maxLength}} caracteres',
            configurationNameRequired: 'Nome da configuração é obrigatório',
            configurationNameTooLong: 'Nome não pode exceder {{maxLength}} caracteres',
            operatingSystemRequired: 'Sistema operacional é obrigatório',
          },
        },
      },
    },
    nodeConfigurationDetail: {
      title: 'Configuração',
      notes: 'Notas',
      updatingNotesFailed: 'Falha ao atualizar notas',
      notesPlaceholder: 'Notas vão aqui',
      header: {
        uploadRevision: 'Enviar Revisão',
        archive: 'Arquivar',
        unarchive: 'Desarquivar',
        disabledNodesAssigned: 'Não é possível arquivar quando atribuído a nós',
        disabledDefaultConfig: 'Não é possível arquivar a configuração padrão',
        errorOnArchive: 'Erro ao arquivar a configuração',
        successOnArchive: 'Configuração arquivada com sucesso',
        successOnUnarchive: 'Configuração desarquivada com sucesso',
        errorOnUnarchive: 'Erro ao desarquivar a configuração',
      },
      tabs: {
        revisions: 'Revisões',
        assignedNodes: 'Nós atribuídos',
        jobsHistory: 'Histórico de empregos',
      },
      assignedNodes: {
        title: 'Nós atribuídos',
        assignConfigToNode: 'Atribuir configuração ao nó',
        table: {
          header: {
            nodes: 'Nós',
            tags: 'Tags',
            configVersion: 'Versão de configuração',
            dateConfigRevisionApplied: 'Ultima atualização (UTC)',
            assginedBy: 'Atribuído por',
            hostName: 'Nome do host',
            nodeVersion: 'Versão do nó',
            os: 'OS',
            connectivity: 'Conectividade',
            revision: 'Revisão',
            configuredBy: 'Configurado por',
            configured: 'Configurado (UTC)',
          },
          processing: 'Processamento',
        },
        noNodesAssigned: 'Nenhum nó atribuído',
      },
      revisionHistory: {
        title: 'Histórico de Revisão',
      },
      revisionsTable: {
        revisionDesription: 'Descrição da revisão',
        author: 'Autor',
        created: 'Criado (UTC)',
      },
      ConfigurationOverview: {
        title: 'Visão geral da configuração',
        configurationId: 'ID de configuração',
        os: 'OS',
        latestRevision: 'Última revisão',
        lastUpdated: 'Ultima atualização',
        windows: 'Windows',
        linux: 'Linux',
      },
    },
    node: {
      overview: 'Visão Geral',
      serverInventory: 'Inventário do Servidor',
      telemetry: 'Telemetria',
      autoRefresh: 'Atualização automática',
      comingSoon: 'Em Breve',
      comingSoonSubtitle: 'Este recurso está atualmente em desenvolvimento.',
      node_description: 'Descrição',
      node_description_placeholder: 'A descrição do nó vai aqui...',
      addTag: 'Adicionar tag',
      server_configuration: 'Configuração do Servidor',
      active_directory: 'Active Directory',
      fqdn: 'FQDN',
      roles: 'Funções',
      yes: 'Sim',
      no: 'Não',
      ip_address: 'Endereço IPv4',
      os: 'OS',
      windows_domain: 'Windows Domain',
      windows_workgroup: 'Windows Workgroup',
      cpuModelName: 'Nome do Modelo da CPU',
      cpuArchitecture: 'Arquitetura da CPU',
      osDescription: 'Descrição',
      osPath: 'Caminho do OS',
      hostname: 'Nome do Host',
      lastUpdatedOn: 'Última atualização em',
      nodeConfiguration: 'Configuração do Nó',
      currentConfiguration: 'Configuração atual',
      archivedConfigurationWarning: 'Arquivado',
      lastUpdatedBy: 'Última atualização por',
      configuration: 'Configuração',
      revision: 'Revisão',
      selectNewConfig: 'Selecione Configuração',
      selectNewRevision: 'Selecione Revisão',
      notConfigured: 'Este nó não está configurado',
      assignConfiguration: 'Atribuir configuração',
      configureNode: 'Configurar nó',
      node_id: 'ID do Nó',
      node_version: 'Versão do nó',
      node_config_id: 'ID da Configuração do Nó',
      config_file: 'Arquivo de Configuração',
      config_file_description: 'Descrição do Arquivo de Configuração',
      node_state: 'Estado do Nó',
      vigilance: 'Vigilância',
      tags: 'Tags',
      credentials: 'Credenciais',
      registered: 'Registrado',
      heartbeats: 'Heartbeats',
      last_configuration_heartbeat: 'Última verificação de configuração',
      last_credentials_heartbeat: 'Última verificação de credenciais',
      unknown_heartbeat: 'Desconhecido',
      cert_expiration: 'Expiração do certificado',
      last_cert_rotation: 'Última rotação de certificado',
      last_seen: 'Última visualização',
      node_status: 'Status',
      node_mode: 'Modo',
      connectivity: 'Conectividade',
      activity: 'Atividade',
      operationalState: 'Estado operacional do nó',
      operationalStateLabel: 'Estado operacional',
      nodeConnectivity: 'Conectividade do nó',
      lifecycle_events: 'Eventos do Ciclo de Vida do Nó',
      events: 'Eventos',
      date: 'Data',
      hallmarks: 'Marcas',
      deflectionsActive: 'Ativo',
      deflectionsInactive: 'Inativo',
      noSystemProfileInfo: 'Incapaz de configurar. Nenhuma informação de perfil do sistema',
      archive_node: 'Arquivar Nó',
      archived: 'Arquivado',
      archiveNodeSuccess: 'Nó arquivado com sucesso',
      archiveNodeError: 'Erro ao arquivar o nó',
      archiveNodeDisabled: 'Este nó já está arquivado',
      archiveNodeMustBeDisconnected: 'O nó deve estar desconectado para ser arquivado',
      nodeArchivedBanner: '{{hostName}} foi arquivado - {{dateArchived}}',
      feedback: {
        operationalStateChanged: 'Estado do nó {{nodeId}} está sendo alterado para {{state}}',
        errorFetchingConfigRevision: 'Erro ao carregar a revisão',
        assignNodeConfigError: 'Erro ao atribuir a configuração ao nó',
        nodeUpdated: 'Nó atualizado',
        updateFailed: 'Erro ao atualizar o nó',
      },
      detections: 'Detecções',
      encryptions: 'Criptografias',
      encryptions_present: 'Criptografias Presentes',
      deflections: 'Deflexões',
      signals: 'Sinais',
      active: 'Ativo',
      inactive: 'Intivo',
      idle: 'Inativo',
      back: 'Voltar para Nós',
      update_baseline: 'Atualizar linha de base',
      clear_hallmarks: 'Limpar Marcas',
      clear_selected: 'Limpar selecionados',
      clear_all: 'Limpar tudo',
      operationalStateChanging:
        'Processando sua solicitação para mudar o estado operacional do nó de {{currentState}} para {{requestedState}}',
      operationalStateChangeRequested: 'Solicitação de mudança de estado operacional',
      operationalStateChangeFailed: 'Falha na mudança de estado operacional',
      operationalStateChangeFailedDescription:
        'Ocorreu um erro ao tentar mudar o estado operacional do nó para {{requestedState}}. Por favor tente novamente. Se o erro persistir, contate o adminstrador do sistema.',
      configAppliedChangeFailed: 'Falha ao atualizar a configuração',
      configAppliedChangeFailedDescription:
        'A configuração deste nó falhou ao ser alterada e permaneceu como {{configName}}. Por favor tente novamente. Se o problema persistir, contate o administrador do sistema.',
      unknown: 'Desconhecido',
      maintenanceMode: 'Modo de Manutenção',
      maintenanceModeOff: 'Desligado',
      maintenanceModeOn: 'Manutenção',
      enterMaintenance: 'Modo de Manutenção',
      endMaintenance: 'Sair do Modo de Manutenção',
      nodeInMaintenanceMode: 'Nó está em modo de manutenção',
      previousConfiguration: 'Configuração anterior',
      enterMaintenanceModal: {
        title: 'Modo de Manutenção',
        description: 'As alterações no seu servidor não serão bloqueadas durante esta janela de manutenção',
        start: 'Iniciar Modo de Manutenção',
        cancel: 'Cancelar',
      },
      exitMaintenanceModal: {
        title: 'Sair do Modo de Manutenção',
        description:
          'Este nó está saindo do Modo de Manutenção. Entrar no Modo de Auditoria a seguir reduzirá seus falsos positivos enquanto o Mimic reaprende o novo estado do seu sistema após as alterações realizadas. No Modo de Auditoria, o Mimic não bloqueará nenhuma ação, mas reportará todas as alterações não autorizadas como sinais, que podem ser usados para atualizar sua configuração de proteção antes de voltar ao Modo Ativo.',
        durationLabel: 'Duração de saída automática (horas):',
        durationHelp: 'O modo de auditoria terminará automaticamente após esta duração.',
        skipDescription: 'Se você deseja pular, as proteções anteriores serão aplicadas conforme configurado.',
        skipAudit: 'Pular Auditoria',
        continue: 'Continuar para Modo de Auditoria',
      },
      nodeInAuditMode: 'Nó está em Modo de Auditoria',
      endAuditMode: 'Sair do Modo de Auditoria',
      auditModeEntered: 'Modo de auditoria iniciado com sucesso',
      auditModeScheduledExit: 'Saída automática em',
      auditModeScheduleWarning:
        'Modo de auditoria iniciado, mas a saída automática não pôde ser agendada. Você precisará sair manualmente do modo de auditoria.',
      auditModeEntryFailed: 'Falha ao entrar no modo de auditoria',
      auditModeError: {
        missingPreviousConfig: 'Não é possível entrar no modo de auditoria: configuração anterior não encontrada',
        jobTimeout: 'A entrada no modo de auditoria expirou. Por favor, tente novamente.',
      },
      maintenanceModeChangeRequested: 'Mudança de modo de manutenção solicitada',
      maintenanceModeChangeFailed:
        'Ocorreu um problema ao aplicar o modo de manutenção. Tente novamente mais tarde ou entre em contato com a equipe de suporte se o problema persistir.',
      maintenanceModeExitFailed:
        'Ocorreu um problema ao sair do modo de manutenção. Tente novamente mais tarde ou entre em contato com a equipe de suporte se o problema persistir.',
      maintenanceModeChanging: 'Processando sua solicitação para alterar o modo de manutenção...',
      maintenanceModeUnsupported: 'O modo de manutenção não é suportado neste nó porque a versão do nó é desconhecida',
      maintenanceModeUnsupportedTooltip:
        'O Modo de Manutenção não é suportado para este Nó, por favor atualize para a versão mais recente do Windows ou Linux',
      connectivityState: {
        connected: 'Conectado',
        disconnected: 'Desconectado',
        degraded: 'Degradado',
        unknown: 'Desconhecido',
      },
      connectivityStateDesc: {
        connected: 'Nó conectado',
        disconnected: 'Nó desconectado',
        degraded: 'Nó degradado',
        unknown: 'Desconhecido',
      },
      hallmarksTable: {
        mimicID: 'Id da Mimic',
        mimicDescription: 'Descrição',
        occurrences: 'Ocorrências',
        firstSeen: 'Primeira Aparição (UTC)',
        lastSeen: 'Última Aparição (UTC)',
      },
      telemetryEventsTable: {
        mimicID: 'Id da Mimic',
        mimicDescription: 'Descrição',
        occurrences: 'Ocorrências',
        firstSeen: 'Primeira Aparição (UTC)',
        lastSeen: 'Última Aparição (UTC)',
        eventID: 'ID do evento',
        eventTime: 'Hora do evento (UTC)',
      },
      telemetryEventDetails: {
        title: {
          hallmarks: 'Detalhes de marca',
          deflections: 'Detalhes de deflexão',
          signals: 'Detalhes do sinal',
        },
      },
      tagDropdown: {
        typeToAddTag: 'Para adicionar uma nova tag, comece a digitar',
        addTagOption: 'Adicione uma nova tag  "{{tagName}}"',
        showMore: 'Mostrar mais {{count}}...',
      },
      hostNameNotAvailable: 'não disponível',
      selectConfiguration: 'Selecione uma configuração',
      selectRevision: 'Selecione uma revisão',
      viewDetails: 'Ver detalhes',
      selectConfigRevisionConfirmation: {
        title: 'Selecionar revisão de configuração',
        description: 'Tem certeza que deseja alterar a revisão de configuração para este nó?',
        yes: 'Sim',
        no: 'Não',
      },
      status: {
        configuringTo: 'Configuring to',
        failedToConfigure: 'Failed to configure to',
        of: 'of',
        revision: 'revision',
      },
      eventsTable: {
        appliedConfig: 'Configurado com sucesso para',
        rejectedConfig: 'Falha ao configurar para',
        pendingConfig: 'Configuração pendente para',
        obtainedConfig: 'Alterando a configuração para',
      },
      clearTelemetryModal: {
        hallmarks: {
          title: 'Limpar Marcas',
          reasonPlaceholder: 'Por favor, forneça uma razão para limpar as marcas',
          buttonTextUnconfirmed: 'Limpar Marcas',
        },
        deflections: {
          title: 'Desvios claros',
          reasonPlaceholder: 'Por favor, forneça uma razão para a limpeza das deflexões.',
          buttonTextUnconfirmed: 'Desvios claros',
        },
        signals: {
          title: 'Sinais claros',
          reasonPlaceholder: 'Por favor, forneça uma razão para limpar os sinais.',
          buttonTextUnconfirmed: 'Sinais claros',
        },
        error: 'Erro ao limpar telemetria',
        reason: 'Razão',
        enterValidReason: 'Por favor, forneça uma razão válida',
        enterValidReasonLength: 'Por favor, forneça uma razão com pelo menos {{min}} caracteres',
        buttonTextPending: 'Limpando...',
        buttonTextConfirmed: 'Marcas limpas!',
      },
    },
    installer: {
      download_installer: 'Baixar o instalador',
      installer: 'instalador',
      done_button: 'Feito',
      steps: {
        default: {
          step1_pre_action: 'Baixe o',
          step1_post_action: '.',
          step2: 'Execute o instalador nos servidores que deseja proteger com um Nó da Mimic.',
          step3:
            'Uma vez instalado, a Mimic enviará telemetria contendo informações sobre o servidor e o nó será adicionado à sua conta.',
        },
      },
    },
    activity: {
      title: 'Atividade',
      sections: {
        hallmarks: 'Indícios',
        deflections: 'Deflexões',
      },
      noWorries: {
        hallmarks: 'Nenhum nó com indícios',
        deflections: 'Nenhum nó com deflexões',
        signals: 'Nenhum nó com sinais',
      },
      allClear: 'Tudo está certo, sem motivo para se preocupar',
      unexpectedError: 'Erro inesperado',
      unexpectedErrorDescription: 'Algo deu errado. Estamos trabalhando para corrigir.',
      lastUpdated: 'Última atualização',
      loading: 'Carregando…',
      loadingMore: 'Carregando mais…',
      failedToLoad: 'Falha ao carregar atividade',
      emptyWindow: 'Nenhum(a) {{section}} na janela selecionada.',
      actions: {
        refresh: '↻ Atualizar',
      },
      table: {
        hostName: 'Nome do Host',
        occurrences: 'Ocorrências',
        connectivity: 'Conectividade',
        firstSeen: 'Primeira Aparição (UTC)',
        lastSeen: 'Última Aparição (UTC)',
      },
      window: {
        '1h': 'Última 1 hora',
        '3h': 'Últimas 3 horas',
        '12h': 'Últimas 12 horas',
        '24h': 'Últimas 24 horas',
        '7d': 'Últimos 7 dias',
        '14d': 'Últimos 14 dias',
        '30d': 'Últimos 30 dias',
      },
      notAvailable: 'Não disponível',
    },
    telemetry: {
      title: 'Telemetria',
      events: 'Eventos',
      noEvents: 'Nenhum evento de telemetria encontrado',
      allClear: 'Tudo está certo, sem motivo para se preocupar',
      notAvailable: 'Não disponível',
      startTime: 'Início (UTC)',
      endTime: 'Fim (UTC)',
      dateRangeExceeded: 'O intervalo de datas não pode exceder 7 dias',
      table: {
        eventType: 'Tipo de Evento',
        hostName: 'Nome do Host',
        mimicId: 'ID Mimic',
        description: 'Descrição',
        processPath: 'Caminho do Processo',
        resourcePath: 'Caminho do Recurso',
        configuration: 'Configuração',
        aclRule: 'Regra ACL',
        startTime: 'Hora de Início (UTC)',
      },
      eventDetails: {
        title: 'Detalhes do Evento de Telemetria',
        eventInfo: 'Informações do Evento',
        timestamp: 'Data e Hora (UTC)',
        node: 'Nó',
        eventType: 'Tipo de Evento',
        mimicId: 'ID Mimic',
        description: 'Descrição',
        processPath: 'Caminho do Processo',
        resourcePath: 'Caminho do Recurso',
        appliedConfig: 'Configuração Aplicada',
        eventPayload: 'Dados do Evento',
        noPayload: 'Nenhum dado detalhado do evento disponível',
        notAvailable: 'N/D',
        configName: 'Nome da Configuração',
        configId: 'ID da Configuração',
        revision: 'Revisão',
        createdBy: 'Criado Por',
      },
    },
    nodeTelemetry: {
      groupedTable: {
        eventType: 'Tipo de Evento',
        events: 'Eventos',
        firstSeen: 'Primeira Ocorrência',
        lastSeen: 'Última Ocorrência',
        truncated: 'Mostrando os primeiros 100 tipos de evento. Ajuste o intervalo de datas para refinar.',
      },
      streamTable: {
        time: 'Hora',
        eventType: 'Tipo de Evento',
        summary: 'Resumo',
        loadMore: 'Carregar mais',
        loadingMore: 'Carregando…',
        openEventDetails: 'Abrir detalhes do evento para {{eventType}} às {{time}}',
      },
      filterBar: {
        preset: {
          last1Hour: 'Última 1 hora',
          last3Hours: 'Últimas 3 horas',
          last12Hours: 'Últimas 12 horas',
          last24Hours: 'Últimas 24 horas',
          last7Days: 'Últimos 7 dias',
          last14Days: 'Últimos 14 dias',
          last30Days: 'Últimos 30 dias',
        },
        eventTypePlaceholder: 'Tipo de evento',
        searchEventTypes: 'Pesquisar tipos de evento...',
        noEventTypes: 'Nenhum encontrado',
        reset: 'Limpar',
      },
      eventsCard: {
        eventsHeader: 'Eventos',
        groupBy: 'Agrupar por:',
        eventTypeChip: 'Tipo de Evento',
        viewMode: {
          stream: 'Fluxo',
          grouped: 'Agrupado',
        },
      },
      eventDetails: {
        title: 'Detalhes do Evento',
        close: 'Fechar detalhes do evento',
        loading: 'Carregando...',
        error: 'Falha ao carregar detalhes do evento',
        eventInfo: {
          title: 'Informações do Evento',
          timestamp: 'Data/hora',
          node: 'Nó',
          eventType: 'Tipo de Evento',
          eventId: 'ID do Evento',
        },
        appliedConfig: {
          title: 'Configuração Aplicada',
          configName: 'Nome da Configuração',
          revision: 'Revisão',
          description: 'Descrição',
          createdBy: 'Criado Por',
        },
        processAncestry: {
          title: 'Ancestralidade do Processo',
          actor: 'ATOR',
          target: 'ALVO',
          empty: 'Nenhum dado de ancestralidade do processo',
        },
        payload: {
          title: 'Payload',
        },
      },
      error: {
        title: 'Não foi possível carregar a telemetria',
        description: 'Não foi possível carregar os dados de telemetria.',
      },
    },
    cliSetup: {
      skipSetup: 'Pular Configuração',
      title: 'Configuração da Interface de Linha de Comando (CLI) da Mimic',
      failedCopy: 'Falha ao copiar o texto',
      failedToGenerate: 'Falha ao gerar o token',
      copy: 'Copiar',
      copied: 'Copiado',
      auth: {
        title: 'Autenticação da CLI com a Mimic',
        downloadLinkText: 'Clique aqui para baixar a CLI.',
        generate: {
          button: 'Gerar token de autenticação',
        },
        step1: {
          number: '1.',
          name: 'Verifique se a Mimic está instalada',
          instructions: 'No terminal, digite',
          command: 'mimic version',
          successPart1: 'Se um número de versão for retornado, por exemplo',
          successExample: 'Mimic 1.0.0',
          successPart2: ', vá para a etapa 2 para autenticar.',
          failurePart1: 'Se você vir qualquer outra coisa, por exemplo',
          failureExample: 'mimic: command not found',
          failurePart2: ', você precisa instalar a CLI.',
        },
        step2: {
          number: '2.',
          name: 'Autentique com a Mimic',
          instructions: 'No terminal, digite',
          command: 'mimic login',
          description:
            'Você será solicitado a inserir um token de autenticação. Para gerar um token, clique no botão abaixo.',
          example: {
            loginCommand: '$ mimic login\nEnter email: {{email}}\nEnter the authentication token: {{token}}',
          },
        },
        help: {
          description: 'Para ver todos os comandos disponíveis, digite',
          command: 'mimic -h',
        },
      },
      download: {
        title: 'Baixe a CLI do Mimic',
        subtitle:
          'Selecione uma opção abaixo para baixar e instalar o binário CLI apropriado para seu sistema operacional.',
        start: 'Download para a CLI {{osType}} {{archType}} iniciado.',
        error: 'Erro ao baixar CLI para {{osType}} {{archType}} ',
        after: {
          span1: 'Depois de instalar,',
          linkText: 'clique aqui para autenticar com Mimic',
        },
        linux: 'Linux',
        windows: 'Windows',
        darwin: 'Mac',
        mac: 'Mac',
        macos: 'Mac',
        arm64: '(Apple Silicon)',
        x86_64: '(Intel)',
      },
      step0: {
        title: 'Bem-vindo à Mimic!',
        body: 'Antes de continuar, sugerimos configurar sua Interface de Linha de Comando (CLI) da Mimic. Isso ajudará você a realizar tarefas comuns da plataforma a partir da linha de comando.',
        start: 'Iniciar Configuração da Mimic CLI',
      },
      step1: {
        stepNumber: 'Passo 1.',
        stepName: 'Baixe a CLI',
        body: 'Selecione uma opção abaixo para baixar e instalar a CLI apropriada para seu sistema operacional.',
        downloadLinux: 'Baixar para Linux',
        downloadWindows: 'Baixar para Windows',
      },
      step2: {
        stepNumber: 'Passo 2.',
        stepName: 'Verifique a instalação',
        body: 'No seu terminal, verifique se a Mimic CLI foi instalada digitando',
        command: 'Lorem ipsum',
        commandCopied: 'Comando copiado para a área de transferência',
        body2: 'Continue para a próxima etapa quando você vir',
        commandSuccess: 'Lorem ipsum',
      },
      step3: {
        stepNumber: 'Passo 3.',
        stepName: 'Autentique com o token',
        body: 'Gere e insira o token abaixo para autenticar com a Mimic.',
        tokenCopied: 'Token copiado para a área de transferência',
      },
      step4: {
        title: 'Parabéns, você configurou com sucesso a Mimic CLI!',
        body: 'Você pode acessar estas instruções novamente em',
        settingsCliSetup: 'Configurações > Mimic CLI',
        continue: 'Continuar para Mimic',
      },
    },
    apiReference: {
      requiredPermission: 'Permissão necessária: {{permission}}',
    },
    error: {
      errorMesage: 'Um erro ocorreu',
      badRequestError: 'Solicitação inválida',
      badRequestErrorDescription: 'A solicitação era inválida ou não pode ser processada',
      forbiddenError: 'Acesso negado',
      forbiddenErrorDescriptionOne: 'Você não tem permissão para acessar esta página.',
      forbiddenErrorDescriptionTwo: 'Entre em contato com seu administrador para obter acesso.',
      unexpectedError: 'Erro inesperado',
      unexpectedErrorDescription: 'Desculpe, algo deu errado',
      notFoundError: 'Página não encontrada',
      notFoundErrorDescription: 'Desculpe, essa página não existe.',
      notFoundContentError: 'Conteúdo não encontrado',
      notFoundContentErrorDescription: 'Desculpe, isso não está disponível',
      conflictError: 'Uma ou mais partes da sua solicitação causar um conflito.',
      conflictErrorDescription: 'Por favor, corrija sua solicitação e tente enviá-la novamente.',
      goBack: 'Volte',
    },
    tenants: {
      errorFetchingTenants: 'Erro ao buscar instâncias',
      errorFetchingTenant: 'Erro ao buscar instância',
    },
    downloadNode: {
      download: 'Baixe {{version}}',
      pleaseWait: 'Por favor, espere',
      noReleases: 'Não há versões disponíveis para esta plataforma.',
      buildType: 'Tipo de Build',
      buildTypeRelease: 'Release',
      buildTypeDebug: 'Debug',
      version: 'Versão',
      flavor: 'Sabor',
      architecture: 'Arquitetura',
      platform: 'Plataforma',
      package: 'Pacote',
      fileSize: 'Tamanho do arquivo',
      hash: 'Hash',
      releaseDate: 'Data de lançamento',
      windows: {
        caption: 'Baixe o Instalador para Windows',
        title: 'Mimic - Proteção Contra Ransomware para Windows',
        description: 'Implante a proteção contra ransomware da Mimic nos seus servidores Windows.',
        viewOtherVersions: 'Ver outras versões do node para Windows',
      },
      linux: {
        caption: 'Baixe o Instalador para Linux',
        title: 'Mimic - Proteção Contra Ransomware para Linux',
        description: 'Implante a proteção contra ransomware da Mimic nos seus servidores Linux.',
        viewOtherVersions: 'Ver outras versões do node para Linux',
      },
      emptyState: {
        title: 'Nenhuma Versão Disponível',
        superadminSubtitle:
          'Nenhuma versão foi publicada para este tenant ainda. Vá para o Gerenciamento de Versões para publicar.',
        userSubtitle: 'Nenhuma versão foi publicada para este tenant ainda. Entre em contato com seu administrador.',
        goToReleaseManagement: 'Ir para Gerenciamento de Versões',
      },
      systemRequirements: {
        title: 'Requisitos do sistema',
        operatingSystem: {
          label: 'Sistema operacional:',
          value: 'Windows Server 2016+',
        },
        designedFor: {
          label: 'Projetado para:',
          value: 'Sistemas Windows x64',
        },
        adminAccess: {
          label: 'Acesso administrativo:',
          value: 'Privilégios de administrador local no servidor Windows',
        },
        diskSpace: {
          label: 'Espaço em disco disponível:',
          value: 'Mínimo de 1GB de espaço em disco',
        },
        memory: {
          label: 'Memória:',
          value: 'Com base nas especificações do fornecedor, o consumo de recursos é de 240-350MB de memória',
        },
        cpu: {
          label: 'CPU:',
          value: '1-2% de um núcleo de CPU',
        },
        network: {
          label: 'Rede:',
          value: 'Acesso à internet para endpoints específicos da Mimic. Conectividade HTTPS (porta 443)',
        },
      },
      installInstructions: {
        title: 'Instruções de instalação',
        step1: {
          prefix: 'Após baixar o instalador, gere um',
          linkText: 'token de instalação',
          suffix: '.',
        },
        msi: {
          step2: {
            text: 'Em seguida, execute o seguinte comando para usar o MSI:',
            command: 'msiexec.exe /i mimic-installer-x64.msi /l*V mimic-install.log TOKEN=<seu-token-de-instalacao>',
          },
        },
        exe: {
          step2: {
            text: 'Em seguida, execute o seguinte comando para usar o EXE:',
            command: 'mimic-installer-x64.exe /verysilent /suppressmsgboxes /token=<seu-token-de-instalacao>',
            graphicalOption: 'Ou para uma instalação gráfica:',
            graphical: 'Clique duas vezes no arquivo executável do instalador e siga as instruções.',
            msiNote: 'Nota: Este download MSI suporta apenas instalação headless.',
            graphicalAlternative: 'Para uma instalação gráfica, por favor, utilize o download do arquivo .exe.',
          },
        },
        step3: {
          prefix: 'Após concluir a instalação, navegue até a',
          linkText: 'página de Nodes',
          suffix: 'para configurar o node.',
        },
      },
    },
    configPrettyViewer: {
      configurationOverview: 'Visão Geral da Configuração',
      noConfigurationData: 'Nenhum dado de configuração disponível',
      unableToParseStructure: 'Não foi possível analisar a estrutura da configuração',
      unrecognizableContent: 'Esta configuração não contém regras de proteção ACL ou motores reconhecíveis',
      rawContentPreview: 'Prévia do conteúdo bruto: {{preview}}...',
      prettyView: 'Visualização Formatada',
      rawCode: 'Código Bruto',
      aclProtection: {
        title: 'Proteção ACL',
        enabled: 'Habilitado',
        disabled: 'Desabilitado',
        rulesGrouped: '{{count}} Regras agrupadas por {{actorCount}} Atores',
        anyActor: 'Qualquer Ator',
        rulesCount: '{{count}} regras',
      },
      engines: {
        title: 'Motores',
        configured: '{{count}} Configurados',
      },
      general: {
        title: 'Configuração Geral',
      },
      buildNew: 'Construir nova revisão',
    },
    grantOAuth: {
      title: 'Autorizar Aplicação',
      description: 'gostaria de acessar sua conta Mimic.',
      permissionsTitle: 'Esta aplicação está solicitando os seguintes escopos:',
      grantButton: 'Conceder Acesso',
      disclaimer:
        'Ao conceder acesso, você permite que esta aplicação consuma a API da Mimic em seu nome com os escopos listados acima.',
      invalidRequest: 'Solicitação OAuth inválida. Parâmetros obrigatórios ausentes.',
    },
    artifactsBrowser: {
      title: 'Artefatos',
      subtitle: 'Navegue e baixe artefatos de compilação do armazenamento em nuvem',
      columns: {
        name: 'Nome',
        size: 'Tamanho',
        lastModified: 'Última Modificação',
        actions: 'Ações',
      },
      download: 'Baixar',
      loadArtifacts: 'Carregar Artefatos',
      loadMore: 'Carregar Mais',
      refresh: 'Atualizar',
      cloudArtifactsBeta: 'Cloud Artifacts Beta',
      root: 'node-artifacts',
      folder: 'Pasta',
      file: 'Arquivo',
      errors: {
        loadFailed: 'Falha ao carregar artefatos',
        downloadFailed: 'Falha ao baixar artefato',
        channelNotAvailable: 'Canal WebSocket não disponível',
      },
    },
    noNodes: {
      title: 'Nenhum Nó Instalado',
      subtitle: 'Comece baixando e instalando um Nó Mimic.',
      downloadNode: 'Baixar Nó',
    },
    releaseManagement: {
      listTitle: 'Gerenciamento de Releases',
      description: 'Gerencie as releases de node disponíveis para este tenant',
      table: {
        os: 'SO',
        version: 'Versão',
        schemaVersion: 'Schema',
        arch: 'Arq',
        dateReleased: 'Lançamento',
        actions: 'Ações',
      },
      filters: {
        latestBuild: 'Apenas Recentes',
      },
      publish: 'Publicar',
      unpublish: 'Despublicar',
      publishSuccess: 'Release publicada com sucesso',
      unpublishSuccess: 'Release despublicada com sucesso',
      publishError: 'Falha ao publicar release',
      unpublishError: 'Falha ao despublicar release',
      copyReleaseId: 'Copiar ID da release',
      copiedReleaseId: 'ID da release copiado para a área de transferência',
    },
    userActivity: {
      title: 'Atividade do Usuário',
      columns: {
        actor: 'Ator',
        type: 'Tipo',
        method: 'Método',
        endpoint: 'Endpoint',
        status: 'Status',
        duration: 'Duração (ms)',
        timestamp: 'Timestamp',
      },
      requestParams: 'Parâmetros de Requisição',
    },
    fleets: {
      title: 'Frotas',
      backToFleets: 'Voltar para Frotas',
      fleetDetails: 'Detalhes da Frota',
      newFleet: 'Nova Frota',
      editFleet: 'Editar Frota',
      editFleetDetails: 'Editar Detalhes da Frota',
      deleteFleet: 'Excluir Frota',
      addNodes: 'Adicionar Nós',
      removeNodes: 'Remover Nós',
      removeNodesCount: 'Remover {{count}} Nó',
      removeNodesCountPlural: 'Remover {{count}} Nós',
      save: 'Salvar',
      cancel: 'Cancelar',
      actions: 'Ações',
      applyConfiguration: 'Aplicar config atual',
      updateConfiguration: 'Atualizar config da frota',
      toggleNodeState: 'Alternar estado do nó',
      toggleNodeMode: 'Alternar modo do nó',
      name: 'Nome',
      os: 'SO',
      fleetId: 'ID da Frota',
      operatingSystem: 'Sistema Operacional',
      windows: 'Windows',
      linux: 'Linux',
      macos: 'macOS',
      configuration: 'Configuração',
      nodeCount: 'Quantidade de Nós',
      configurationStatus: 'Status de Configuração',
      synced: 'Sincronizado',
      outOfSync: 'Fora de Sincronia',
      createdUtc: 'Criado (UTC)',
      description: 'Descrição',
      noDescriptionProvided: 'Nenhuma descrição fornecida',
      nodes: 'Nós',
      form: {
        fleetName: 'Nome da Frota',
        fleetNamePlaceholder: 'Digite o nome da frota',
        description: 'Descrição',
      },
      feedback: {
        fleetUpdatedSuccess: 'Frota atualizada com sucesso',
        fleetUpdatedError: 'Falha ao atualizar frota',
        nodesAddedSuccess: 'Nós adicionados com sucesso',
        nodesAddedError: 'Falha ao adicionar nós',
        nodesRemovedSuccess: '{{count}} nó removido com sucesso',
        nodesRemovedSuccessPlural: '{{count}} nós removidos com sucesso',
        nodesRemovedError: 'Falha ao remover nós',
        fleetDeletedSuccess: 'Frota excluída com sucesso',
        fleetDeletedError: 'Falha ao excluir frota',
        configAppliedSuccess: 'Configuração aplicada a todos os nós',
        configAppliedError: 'Falha ao aplicar configuração',
      },
      deleteModal: {
        title: 'Excluir esta frota?',
        delete: 'Excluir',
        body: 'A exclusão da frota não é destrutiva. Todos os nós persistirão independentemente após a remoção da frota.',
      },
      addNodesDrawer: {
        title: 'Adicionar Nós à Frota',
        selectNodesWindows:
          'Selecione os nós para adicionar a esta frota. Apenas nós Windows que não estão em uma frota são exibidos.',
        selectNodesLinux:
          'Selecione os nós para adicionar a esta frota. Apenas nós Linux que não estão em uma frota são exibidos.',
        addNodesCount: 'Adicionar {{count}} Nós',
        addNodes: 'Adicionar Nós',
      },
      updateConfigDrawer: {
        fleet: 'Frota',
        steps: {
          selectConfig: 'Selecionar Configuração',
          selectRevision: 'Selecionar Revisão',
          review: 'Revisar Alterações',
        },
        updateConfig: 'Atualizar Config',
        review: {
          title: 'Revisar Atualização de Configuração',
          fleet: 'Frota',
          nodesAffected: 'Nós Afetados',
          currentConfig: 'Configuração Atual',
          newConfig: 'Nova Configuração',
        },
        feedback: {
          success: 'Configuração da frota atualizada com sucesso',
          error: 'Falha ao atualizar configuração da frota',
        },
      },
      tabs: {
        configured: 'Configurados',
        fleets: 'Frotas',
        pending: 'Pendentes',
        archived: 'Arquivados',
      },
      wizard: {
        steps: {
          details: 'Nova Frota',
          selectConfig: 'Selecionar Configuração',
          selectRevision: 'Selecionar Revisão',
          selectNodes: 'Selecionar Nós',
          review: 'Revisar',
        },
        previous: 'Anterior',
        next: 'Próximo',
        createFleet: 'Criar Frota',
        form: {
          name: 'Nome',
          nameRequired: 'Nome é obrigatório',
          nameMaxLength: 'Nome deve ter no máximo {{max}} caracteres',
          namePlaceholder: 'Digite o nome da frota',
          description: 'Descrição',
          operatingSystem: 'Sistema Operacional',
          operatingSystemRequired: 'Sistema operacional é obrigatório',
          operatingSystemPlaceholder: 'Selecione o sistema operacional',
        },
        review: {
          fleetDetails: 'Detalhes da Frota',
          name: 'Nome',
          description: 'Descrição',
          operatingSystem: 'Sistema Operacional',
          configuration: 'Configuração',
          revision: 'Revisão',
          configId: 'ID da Configuração',
          selectedNodes: 'Nós Selecionados ({{count}})',
          noNodesSelected: 'Nenhum nó selecionado',
        },
        feedback: {
          fleetCreatedSuccess: 'Frota criada com sucesso',
          fleetCreatedError: 'Falha ao criar frota',
        },
      },
    },
  },
};
